/**
 * Low-balance warning thresholds and messaging, shared by the app-shell credit
 * pill and the credits page. Purely a frontend UX nudge — the authoritative
 * gate is the backend `InsufficientCreditsError` raised in `chargeForTask`.
 *
 * The cheapest billable task (GENERATE_SIZE / EDIT_REGENERATE) costs 100
 * credits, so a balance under 500 leaves room for only a handful of sizes —
 * a sensible point to start nudging the user toward a top-up.
 */
import i18n from "@/i18n";

export const LOW_CREDIT_THRESHOLD = 500;

export type CreditWarningLevel = "ok" | "low" | "empty";

export function creditWarningLevel(total: number): CreditWarningLevel {
  if (total <= 0) return "empty";
  if (total < LOW_CREDIT_THRESHOLD) return "low";
  return "ok";
}

/** Short label for the credit pill (sidebar), localized to the active language. */
export function creditWarningPillText(level: CreditWarningLevel): string | null {
  if (level === "empty") return i18n.t("app:creditWarning.pill.empty");
  if (level === "low") return i18n.t("app:creditWarning.pill.low");
  return null;
}

/** Title + guidance for the credits-page banner, localized to the active language. */
export function creditWarningBanner(
  level: CreditWarningLevel,
): { title: string; body: string } | null {
  if (level === "empty") {
    return {
      title: i18n.t("app:creditWarning.banner.empty.title"),
      body: i18n.t("app:creditWarning.banner.empty.body"),
    };
  }
  if (level === "low") {
    const locale = i18n.language === "vi" ? "vi-VN" : "en-US";
    return {
      title: i18n.t("app:creditWarning.banner.low.title"),
      body: i18n.t("app:creditWarning.banner.low.body", {
        threshold: LOW_CREDIT_THRESHOLD.toLocaleString(locale),
      }),
    };
  }
  return null;
}
